import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { ChevronDown, ChevronRight, Shield, Lock, Users, Globe, Mail } from "lucide-react";

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (id) => {
    setExpandedSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const CollapsibleSection = ({ id, title, icon: Icon, children }) => {
    const isExpanded = expandedSections[id];
    return (
      <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden transition-shadow duration-300 hover:shadow-2xl">
        <button
          onClick={() => toggleSection(id)}
          className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-black/30 to-black/10 hover:from-black/40 hover:to-black/20 transition"
        >
          <div className="flex items-center space-x-3 text-white">
            <Icon className="w-5 h-5 text-gray-400" />
            <h2 className="text-lg font-light tracking-wide">{title}</h2>
          </div>
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-400" />
          )}
        </button>
        {isExpanded && (
          <div className="px-6 py-5 text-gray-300 leading-relaxed space-y-4 border-t border-white/10">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-black to-black text-white font-light border-b">
      <Helmet>
        <title>Privacy Policy | Frost & Grey</title>
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 text-center max-w-4xl mx-auto px-6">
        <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-6 mx-auto">
        </div>
        <h1 className="text-5xl font-thin mb-4 tracking-tight">
          Privacy <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">Policy</span>
        </h1>
        <p className="max-w-xl mx-auto text-gray-400 text-lg mb-6">
          Your privacy is our utmost priority. Discover how Frost & Grey protects and respects your data across all services.
        </p>
        <p className="text-gray-500 text-sm tracking-widest uppercase">
          Last updated: June 16, 2025
        </p>
      </section>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-6 pb-20 space-y-8">
        <CollapsibleSection id="intro" title="1. Introduction & Scope" icon={Shield}>
          <p>
            Frost & Grey is committed to safeguarding your privacy and ensuring transparency in how we collect, use, and protect your personal data across all AI training, consulting, and digital services.
          </p>
          <p>
            This policy applies globally, reflecting our dedication to compliance and respect for your rights.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="data-collection" title="2. Data We Collect" icon={Lock}>
          <ul className="list-disc list-inside space-y-2">
            <li><strong>Personal Info:</strong> Name, email, contact details, professional background.</li>
            <li><strong>Usage Data:</strong> Interaction with our platforms and services.</li>
            <li><strong>Training Data:</strong> Course progress, assessments, and feedback.</li>
            <li><strong>Technical Data:</strong> Device and browser info, IP addresses.</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="usage" title="3. How We Use Your Data" icon={Users}>
          <ul className="list-disc list-inside space-y-2">
            <li>Deliver personalized AI training and consulting services.</li>
            <li>Communicate updates, offers, and support.</li>
            <li>Ensure security, prevent fraud, and comply with legal obligations.</li>
            <li>Improve our services through analytics and feedback.</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="sharing" title="4. Information Sharing" icon={Globe}>
          <p>
            We never sell your personal data. We share information only with trusted partners under strict agreements to support service delivery and legal compliance.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="rights" title="5. Your Privacy Rights" icon={Users}>
          <ul className="list-disc list-inside space-y-2">
            <li>Access, correct, or delete your personal data.</li>
            <li>Opt-out of marketing communications.</li>
            <li>Request data portability or restriction.</li>
            <li>Contact our Data Protection Officer at <a href="mailto:privacy@frostgrey.com" className="underline text-cyan-400">privacy@frostgrey.com</a>.</li>
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="security" title="6. Data Security" icon={Lock}>
          <p>
            We implement industry-leading security measures including encryption, access controls, and regular audits to protect your data.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="contact" title="7. Contact & Support" icon={Mail}>
          <p>
            For privacy inquiries or concerns, please reach out to our Data Protection Officer at <a href="mailto:privacy@frostgrey.com" className="underline text-indigo-400">privacy@frostgrey.com</a>.
          </p>
        </CollapsibleSection>
      </main>
    </div>
  );
};

export default PrivacyPolicy;

